<script>
import { mapActions } from 'vuex';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import { useAlert } from 'dashboard/composables';
import CreateProductModal from './CreateProductModal.vue';
import deleteConfirmationModal from '../../branches/components/deleteConfirmationModal.vue';
import ProductCard from './ProductCard.vue';
import OverrideCard from './OverrideCard.vue';
import CreateOverrideModal from './CreateOverrideModal.vue';

export default{
  name: "ViewBranch",
  components: {
    LoadingState,
    CreateProductModal,
    deleteConfirmationModal,
    ProductCard,
    OverrideCard,
    CreateOverrideModal
  },
  data() {
    return {
      isLoading: false,
      isError: false,
      errorMessage: '',
      branch: null,
      daysOfWeek: [
        { label: 'Sun', value: 'Sun' },
        { label: 'Mon', value: 'Mon' },
        { label: 'Tue', value: 'Tue' },
        { label: 'Wed', value: 'Wed' },
        { label: 'Thu', value: 'Thu' },
        { label: 'Fri', value: 'Fri' },
        { label: 'Sat', value: 'Sat' },
      ],
      showCreateModal: false,
      showEditProductModal: false,
      productToEdit: null,
      // Product state
      products: [],
      productPage: 1,
      productPageSize: 10,
      productTotal: 0,
      isProductLoading: false,
      isProductError: false,
      productErrorMessage: '',
      isProductEmpty: false,
      showDeleteProductModal: false,
      productToDelete: null,
      overrides: [],
      overridePage: 1,
      overridePageSize: 10,
      overrideTotal: 0,
      isOverrideLoading: false,
      isOverrideError: false,
      overrideErrorMessage: '',
      isOverrideEmpty: false,
      showCreateOverride: false,
      showEditOverride: false,
      overrideToEdit: false,
      showDeleteOverrideModal: false,
      overrideToDelete: null,
    };
  },
  computed: {
    branchDays() {
      if (!this.branch || !this.branch.operating_days) return [];
      return this.branch.operating_days.split(',');
    },
    productTotalPages() {
      return Math.ceil(this.productTotal / this.productPageSize);
    },
    overrideTotalPages() {
      return Math.ceil(this.overrideTotal / this.overridePageSize);
    },
  },
  methods: {
    ...mapActions('rentalBranch', ['getRentalBranchById', 'getActiveProducts', 'deleteProduct', 'fetchOverrides', 'deleteOverride']),
    goBackToKnowledgeBase() {
      this.$router.back();
    },
    formatPricing(pricing) {
      if (!pricing) return '-';
      let arr = pricing;
      if (typeof pricing === 'string') {
        try {
          arr = JSON.parse(pricing);
        } catch {
          return pricing;
        }
      }
      if (!Array.isArray(arr) || arr.length === 0) return '-';
      // Return array for rendering
      return arr.map(p => `${p.currency} ${p.cost} per ${p.per_value > 1 ? p.per_value + ' ' : ''}${p.per_unit}`);
    },
    async fetchBranch() {
      this.isLoading = true;
      this.isError = false;
      this.errorMessage = '';
      try {
        const branch_id = this.$route.params.branchId;
        const res = await this.getRentalBranchById({ branch_id });
        this.branch = res;
      } catch (err) {
        this.isError = true;
        this.errorMessage = err.message || 'Failed to load branch details.';
      } finally {
        this.isLoading = false;
      }
    },
    async fetchProducts() {
      this.isProductLoading = true;
      this.isProductError = false;
      this.productErrorMessage = '';
      try {
        const branch_id = this.$route.params.branchId;
        const res = await this.getActiveProducts({ branch_id, page: this.productPage, page_size: this.productPageSize });
        this.products = res.results || [];
        this.productTotal = res.total || 0;
        this.isProductEmpty = this.products.length === 0;
      } catch (err) {
        this.isProductError = true;
        this.productErrorMessage = err.message || 'Failed to load products.';
      } finally {
        this.isProductLoading = false;
      }
    },
    async getOverrides() {
      this.isOverrideLoading = true;
      this.isOverrideError = false;
      this.overrideErrorMessage = '';
      try {
        const branch_id = this.$route.params.branchId;
        const res = await this.fetchOverrides({ branch_id, page: this.overridePage, page_size: this.overridePageSize });
        this.overrides = res.results || [];
        this.overrideTotal = res.total || 0;
        this.isOverrideEmpty = this.overrides.length === 0;
      } catch (err) {
        this.isOverrideError = true;
        this.overrideErrorMessage = err.message || 'Failed to load overrides.';
      } finally {
        this.isOverrideLoading = false;
      }
    },
    handleOverridePageChange(newPage) {
      if (newPage >= 1 && newPage <= this.overrideTotalPages) {
        this.overridePage = newPage;
        this.getOverrides();
      }
    },
    handleOverridePageSizeChange(e) {
      this.overridePageSize = Number(e.target.value);
      this.overridePage = 1;
      this.getOverrides();
    },
    handleProductPageChange(newPage) {
      if (newPage >= 1 && newPage <= this.productTotalPages) {
        this.productPage = newPage;
        this.fetchProducts();
      }
    },
    handleProductPageSizeChange(e) {
      this.productPageSize = Number(e.target.value);
      this.productPage = 1;
      this.fetchProducts();
    },
    openDeleteProductModal(product) {
      this.productToDelete = product;
      this.showDeleteProductModal = true;
    },
    openEditProductModal(product) {
      this.productToEdit = product;
      this.showEditProductModal = true;
    },
    async handleDeleteProduct() {
      if (!this.productToDelete) return;
      try {
        await this.deleteProduct({ product_id: this.productToDelete.product_id });
        this.showDeleteProductModal = false;
        this.productToDelete = null;
        useAlert('Product deleted successfully');
        this.fetchProducts();
      } catch (err) {
        this.productErrorMessage = err.message || 'Failed to delete product.';
        this.showDeleteProductModal = false;
        this.productToDelete = null;
      }
    },
    handleDeleteOverride(override) {
      this.overrideToDelete = override;
      this.showDeleteOverrideModal = true;
    },
    async confirmDeleteOverride() {
      if (!this.overrideToDelete) return;
      try {
        const branch_id = this.$route.params.branchId;
        const override_id = this.overrideToDelete.override_id || this.overrideToDelete.id;
        await this.deleteOverride({ branch_id, override_id });
        this.showDeleteOverrideModal = false;
        this.overrideToDelete = null;
        useAlert('Override deleted successfully');
        this.getOverrides();
      } catch (err) {
        this.showDeleteOverrideModal = false;
        this.overrideToDelete = null;
        useAlert(err.message || 'Failed to delete override.');
      }
    },
  },
  mounted() {
    this.fetchBranch();
    this.fetchProducts();
    this.getOverrides();
  }
}
</script>

<template>
  <div class="p-4 md:p-8 flex flex-1 h-full flex-col m-0 overflow-auto bg-slate-50 dark:bg-slate-900 transition-colors duration-200">
    <!-- Header with back button, title, and actions -->
    <header class="mb-6">
      <div class="flex items-center mb-2">
        <woot-button icon="chevron-left" @click="goBackToKnowledgeBase" variant="clear" color-scheme="secondary" />
        <h1 class="text-2xl font-medium text-slate-900 dark:text-slate-100 transition-colors">Rental Branch Details</h1>
      </div>
      <p class="text-slate-600 dark:text-slate-400 mb-4 transition-colors">
        View and manage the details of your rental branch.
      </p>
    </header>

    <CreateOverrideModal v-if="showCreateOverride" :existing-overrides="overrides" @close="showCreateOverride = false" @created="getOverrides" :available-days="branchDays" />
    <!-- <CreateOverrideModal v-if="showEditOverride" :override="overrideToEdit" @close="showEditOverride = false" @created="fetchOverrides" /> -->
    <CreateProductModal v-if="showCreateModal" @created="fetchProducts(); showCreateModal = false" @close="showCreateModal = false"/>
    <CreateProductModal v-if="showEditProductModal && productToEdit" :editMode="true" :product="productToEdit" @close="showEditProductModal = false; productToEdit = null" @updated="fetchProducts(); showEditProductModal = false; productToEdit = null"/>
    <div v-if="showDeleteProductModal && productToDelete" class="z-50 fixed inset-0 flex items-center justify-center bg-slate-800/40 px-4">
      <delete-confirmation-modal
        :deleteName="productToDelete.product_name"
        @close="showDeleteProductModal = false"
        @confirm-delete="handleDeleteProduct"
      />
    </div>
    <div v-if="showDeleteOverrideModal && overrideToDelete" class="z-50 fixed inset-0 flex items-center justify-center bg-slate-800/40 px-4">
      <delete-confirmation-modal
        :deleteName="'Override for ' + (overrideToDelete.override_date || overrideToDelete.date)"
        @close="showDeleteOverrideModal = false"
        @confirm-delete="confirmDeleteOverride"
      />
    </div>

    <!-- Loading state -->
    <LoadingState v-if="isLoading" message="Loading branch details..." />
    <!-- Error state -->
    <div v-if="isError && !isLoading" class="flex flex-col items-center justify-center flex-1 py-12">
      <fluent-icon icon="error-circle" class="w-32 h-32 mb-4 text-red-500" />
      <p class="text-lg text-red-500 dark:text-red-400">{{ errorMessage }}</p>
    </div>

    <div v-if="branch && !isLoading && !isError" class="flex flex-col gap-2">
      <!-- Branch Name -->
      <div class="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">{{ branch.branch_name }}</div>
      <!-- Check-in and Check-out Time -->
      <div class="flex gap-4 items-center mb-2">
        <div class="flex items-center gap-2 border border-slate-100 p-2 rounded-md bg-white">
          <span class="font-semibold text-slate-700 dark:text-slate-200">Check-in:</span>
          <span class="text-base text-slate-800 dark:text-slate-100">{{ branch.check_in_time }}</span>
        </div>
        <div class="flex items-center gap-2 border border-slate-100 p-2 rounded-md bg-white">
          <span class="font-semibold text-slate-700 dark:text-slate-200">Check-out:</span>
          <span class="text-base text-slate-800 dark:text-slate-100">{{ branch.check_out_time }}</span>
        </div>
      </div>
      <!-- Available Days -->
      <div class="flex gap-2 px-2 items-center mb-2">
        <span class="font-semibold text-slate-700 dark:text-slate-200 mr-2">Available Days:</span>
        <div class="flex gap-1">
          <span v-for="day in daysOfWeek" :key="day.value"
            :class="branchDays.includes(day.value) ? 'bg-green-200 text-green-800 dark:bg-green-700 dark:text-green-100' : 'bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-300'"
            class="px-3 py-1 rounded text-sm font-medium">
            {{ day.label }}
          </span>
        </div>
      </div>

      <div class="mt-2">
        <div class="flex flex-col lg:flex-row gap-6">
          <!-- Product list -->
          <div class="flex-[2]">
            <span class="flex justify-between items-center mb-4 gap-4">
              <h2 class="text-xl font-semibold text-slate-900 dark:text-slate-100">Products</h2>
              <woot-button icon="add" @click="showCreateModal = true">Create Product</woot-button>
            </span>

            <LoadingState v-if="isProductLoading" message="Loading products..." />

            <div
              v-if="isProductError && !isProductLoading"
              class="flex flex-col items-center justify-center flex-1 py-12"
            >
              <fluent-icon icon="error-circle" class="w-32 h-32 mb-4 text-red-500" />
              <p class="text-lg text-red-500 dark:text-red-400">{{ productErrorMessage }}</p>
            </div>

            <div
              v-if="isProductEmpty && !isProductLoading && !isProductError"
              class="flex flex-col items-center justify-center flex-1 py-12"
            >
              <p class="text-lg text-slate-500 dark:text-slate-400">No products found for this branch.</p>
            </div>

            <div v-if="!isProductLoading && !isProductError && !isProductEmpty">
              <div class="flex justify-between items-center mb-2">
                <div class="flex gap-2 items-center">
                  <label for="productPageSize" class="text-sm">Page size:</label>
                  <select
                    id="productPageSize"
                    v-model="productPageSize"
                    @change="handleProductPageSizeChange"
                    class="border rounded px-2 py-1"
                  >
                    <option v-for="size in [5,10,20,50]" :key="size" :value="size">{{ size }}</option>
                  </select>
                </div>
                <div class="flex gap-2 items-center">
                  <woot-button
                    :disabled="productPage === 1"
                    @click="handleProductPageChange(productPage - 1)"
                    variant="smooth"
                    color-scheme="primary"
                  >
                    Prev
                  </woot-button>
                  <span class="text-sm">Page {{ productPage }} of {{ productTotalPages }}</span>
                  <woot-button
                    :disabled="productPage === productTotalPages || productTotalPages === 0"
                    @click="handleProductPageChange(productPage + 1)"
                    variant="smooth"
                    color-scheme="primary"
                  >
                    Next
                  </woot-button>
                </div>
              </div>
              <div class="space-y-4">
                <ProductCard
                  v-for="product in products"
                  :key="product.product_id"
                  :product="product"
                  :formatPricing="formatPricing"
                  :openEditProductModal="openEditProductModal"
                  :openDeleteProductModal="openDeleteProductModal"
                />
              </div>
            </div>
          </div>

          <!-- Override list -->
          <div class="flex-[1]">
            <span class="flex justify-between items-center mb-4 gap-4">
              <h2 class="text-xl font-semibold text-slate-900 dark:text-slate-100">Overrides</h2>
              <woot-button icon="add" @click="showCreateOverride = true">Create Override</woot-button>
            </span>

            <div v-if="isOverrideLoading" class="py-8 text-center">
              <LoadingState message="Loading overrides..." />
            </div>
            <div v-else-if="isOverrideError" class="flex flex-col items-center justify-center flex-1 py-12">
              <fluent-icon icon="error-circle" class="w-32 h-32 mb-4 text-red-500" />
              <p class="text-lg text-red-500 dark:text-red-400">{{ overrideErrorMessage }}</p>
            </div>
            <div v-else-if="isOverrideEmpty" class="flex flex-col items-center justify-center flex-1 py-12">
              <p class="text-lg text-slate-500 dark:text-slate-400">No overrides found for this branch.</p>
            </div>
            <div v-else>
              <div class="flex justify-between items-center mb-2">
                <div class="flex gap-2 items-center">
                  <label for="overridePageSize" class="text-sm">Page size:</label>
                  <select
                    id="overridePageSize"
                    v-model="overridePageSize"
                    @change="handleOverridePageSizeChange"
                    class="border rounded px-2 py-1"
                  >
                    <option v-for="size in [5,10,20,50]" :key="size" :value="size">{{ size }}</option>
                  </select>
                </div>
                <div class="flex gap-2 items-center">
                  <woot-button
                    :disabled="overridePage === 1"
                    @click="handleOverridePageChange(overridePage - 1)"
                    variant="smooth"
                    color-scheme="primary"
                  >
                    Prev
                  </woot-button>
                  <span class="text-sm">Page {{ overridePage }} of {{ overrideTotalPages }}</span>
                  <woot-button
                    :disabled="overridePage === overrideTotalPages || overrideTotalPages === 0"
                    @click="handleOverridePageChange(overridePage + 1)"
                    variant="smooth"
                    color-scheme="primary"
                  >
                    Next
                  </woot-button>
                </div>
              </div>
              <div class="space-y-4">
                <OverrideCard
                  v-for="item in overrides"
                  :key="item.id || item.override_id"
                  :override="item"
                  @delete="handleDeleteOverride"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>